import { useRef } from "react";
import {
  Box, Flex, HStack, Button, Icon,
  Input, InputGroup, InputLeftElement,
  Table, Thead, Tbody, Tr, Th, Td,
  Text, Badge, IconButton,
  AlertDialog, AlertDialogBody, AlertDialogFooter,
  AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
  useColorModeValue,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, AddIcon } from "@chakra-ui/icons";
import { Search } from "lucide-react";
import { usePermissionsAdmin } from "./usePermissionsAdmin";
import { PermissionFormModal } from "./PermissionFormModal";
import { ChildrenInteractionBlocker } from "../../../components/overlays/ChildrenInteractionBlocker";
import { PERMISSIONS } from "../../../config/permissions";
import { ErrorState } from "../../../components/ErrorState";
import { ListPagination } from "../../rh/onboarding/components/ListPagination";
import { OnflyLoading } from "../../../tools/OnflyLoading";

export function PermissionsAdminPage() {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const subtleColor = useColorModeValue("#84858c", "#718096");
  const thColor     = useColorModeValue("#84858c", "#718096");
  const cardBg      = useColorModeValue("white", "#0f2040");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const theadBg     = useColorModeValue("rgba(0,158,251,0.04)", "rgba(0,158,251,0.06)");
  const rowHoverBg  = useColorModeValue("rgba(0,158,251,0.025)", "rgba(0,158,251,0.04)");
  const inputBg     = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const codeBg      = useColorModeValue("rgba(0,158,251,0.08)", "rgba(0,158,251,0.15)");

  const {
    permissions,
    loading,
    error,
    search,
    setSearch,
    page,
    totalPages,
    setPage,
    isFormOpen,
    setIsFormOpen,
    selectedPermission,
    deleteTargetId,
    handleCreateOpen,
    handleEditOpen,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    refreshList,
  } = usePermissionsAdmin();

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="flex-start" mb={6} gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight={700} color={titleColor} mb={1}>
            Gerenciamento de Permissões
          </Text>
          <Text fontSize="sm" color={descColor}>
            Defina os códigos de acesso que compõem os papéis da plataforma.
          </Text>
        </Box>
        <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_PERMISSIONS]}>
          <Button
            leftIcon={<Icon as={AddIcon} />}
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            h="40px"
            px={5}
            fontSize="14px"
            fontWeight={500}
            onClick={handleCreateOpen}
            flexShrink={0}
          >
            Nova Permissão
          </Button>
        </ChildrenInteractionBlocker>
      </Flex>

      <InputGroup maxW="380px" mb={6}>
        <InputLeftElement pointerEvents="none">
          <Icon as={Search} boxSize={4} color={subtleColor} />
        </InputLeftElement>
        <Input
          placeholder="Buscar pelo código da permissão..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg={inputBg}
          borderColor={inputBorder}
          borderRadius="8px"
          fontSize="sm"
          _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
          _placeholder={{ color: subtleColor }}
        />
      </InputGroup>

      {loading ? (
        <Flex justify="center" align="center" py={16}>
          <OnflyLoading containerSize={80} />
        </Flex>
      ) : error ? (
        <ErrorState onRetry={refreshList} />
      ) : permissions.length === 0 ? (
        <Text color={subtleColor} fontSize="sm">Nenhuma permissão encontrada.</Text>
      ) : (
        <Box
          bg={cardBg}
          borderRadius="12px"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="0px 4px 8px 0px rgba(14, 61, 93, 0.08)"
          overflow="hidden"
          overflowX="auto"
        >
          <Table variant="simple" minW="520px">
            <Thead bg={theadBg}>
              <Tr>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3} w="60px">ID</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Código</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Descrição</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Papéis</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" textTransform="none" py={3}>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {permissions.map((perm) => (
                <Tr key={perm.id} _hover={{ bg: rowHoverBg }} transition="background 0.12s">
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontSize="sm" color={subtleColor}>{perm.id}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Badge
                      bg={codeBg}
                      color="#009efb"
                      fontFamily="mono"
                      fontSize="11px"
                      fontWeight={700}
                      letterSpacing="0.5px"
                      px={2.5}
                      py={0.5}
                      borderRadius="6px"
                      border="1px solid rgba(0,158,251,0.2)"
                    >
                      {perm.code}
                    </Badge>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontSize="sm" color={descColor}>{perm.description || "—"}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Badge
                      borderRadius="50px"
                      fontSize="11px"
                      fontWeight={600}
                      px={2.5}
                      py={0.5}
                      bg={perm.roles_count > 0 ? "rgba(0,164,110,0.12)" : "rgba(0,0,0,0.06)"}
                      color={perm.roles_count > 0 ? "#00a46e" : subtleColor}
                    >
                      {perm.roles_count} {perm.roles_count === 1 ? "papel" : "papéis"}
                    </Badge>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={0}>
                      <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_PERMISSIONS]}>
                        <IconButton
                          aria-label="Editar"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          color="#009efb"
                          _hover={{ bg: "rgba(0,158,251,0.08)" }}
                          onClick={() => handleEditOpen(perm)}
                        />
                      </ChildrenInteractionBlocker>
                      <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_PERMISSIONS]}>
                        <IconButton
                          aria-label="Excluir"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          color={subtleColor}
                          _hover={{ bg: "rgba(220,53,69,0.08)", color: "#dc3545" }}
                          onClick={() => handleDeleteClick(perm.id)}
                        />
                      </ChildrenInteractionBlocker>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />

      <PermissionFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        permission={selectedPermission}
        onSuccess={refreshList}
      />

      <AlertDialog isOpen={deleteTargetId !== null} leastDestructiveRef={cancelRef} onClose={cancelDelete}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="md" fontWeight={600}>Confirmar exclusão</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Tem certeza que deseja excluir esta permissão? Esta ação não pode ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button ref={cancelRef} variant="ghost" size="sm" borderRadius="50px" onClick={cancelDelete}>
                Cancelar
              </Button>
              <Button colorScheme="red" size="sm" borderRadius="50px" onClick={confirmDelete}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
